import FlexFormContainer from "../common/FlexFormContainer";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  Divider,
  Flex,
  Heading,
  SelectField,
  SwitchField,
  Text,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { TagsInput } from "react-tag-input-component";
import { useNavigate, useParams } from "react-router-dom";
import { useUserQuery } from "../common/useUserQuery";
import { useAllergiesPutMutation } from "./api/useAllergiesMutation";
import { useAllergyQuery } from "./api/useAllergiesQuery";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { putAllergySchema } from "./validations/allergiesValidations";

export default function AllergiesEditPage() {
  const navigate = useNavigate();
  const { data: userQueryData } = useUserQuery();
  const params = useParams();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: zodResolver(putAllergySchema) });

  const { mutate: allergiesPutMutation } = useAllergiesPutMutation();
  const { data } = useAllergyQuery(params.id, userQueryData?.patientUserId);

  useEffect(() => {
    if (data) {
      reset({
        allergyName: data?.allergyName,
        allergyDescription: data?.allergyDescription,
        allergyLevel: data?.allergyLevel,
        reactions: JSON.parse(data?.reactions),
        derivatives: JSON.parse(data?.derivatives),
        isActive: data?.isActive,
      });
    }
  }, [data]);

  const onSubmit = (values) => {
    allergiesPutMutation({
      id: params.id,
      ...values,
    });
    reset();
    navigate("/user/hr/allergies");
  };
  if (data) {
    return (
      <>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FlexFormContainer headerTitle={"Allergy Details"}>
            <Flex
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="allergyName"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Allergy Name"
                    {...field}
                    value={field.value || ""}
                    errorMessage={errors.allergyName?.message}
                    hasError={!!errors.allergyName}
                  />
                )}
              />
              <Controller
                name="allergyDescription"
                control={control}
                render={({ field }) => (
                  <TextAreaField
                    minWidth="300px"
                    label="Allergy Description"
                    {...field}
                    value={field.value || ""}
                    errorMessage={errors.allergyDescription?.message}
                    hasError={!!errors.allergyDescription}
                  />
                )}
              />
              <Controller
                name="allergyLevel"
                control={control}
                render={({ field }) => (
                  <SelectField
                    minWidth="300px"
                    label="Allergy Level"
                    placeholder="Select"
                    {...field}
                    errorMessage={errors.allergyLevel?.message}
                    hasError={!!errors.allergyLevel}
                  >
                    <option value="Type I or anaphylactic reactions">
                      Type I or anaphylactic reactions
                    </option>
                    <option value="Type II or cytotoxic reactions">
                      Type II or cytotoxic reactions
                    </option>
                    <option value="Type III or immunocomplex reactions">
                      Type III or immunocomplex reactions
                    </option>
                    <option value="Type IV or cell-mediated reactions">
                      Type IV or cell-mediated reactions
                    </option>
                  </SelectField>
                )}
              />
              <Divider size="small" />
              <Heading level={7}>Reactions:</Heading>
              <Controller
                name="reactions"
                control={control}
                render={({ field }) => {
                  {
                    !!errors.reactions && (
                      <Text
                        variation="error"
                        as="p"
                        lineHeight="0.5em"
                        fontWeight={400}
                        fontSize="0.8em"
                        fontStyle="normal"
                        textDecoration="none"
                        width="30vw"
                        style={{ marginLeft: "10px" }}
                      >
                        {errors.reactions.message}
                      </Text>
                    );
                  }
                  return (
                    <TagsInput
                      placeHolder="Tags"
                      value={field?.value || []}
                      onChange={(tags) => field.onChange(tags)}
                    />
                  );
                }}
              />
              <Divider size="small" />
              <Heading level={7}>Allergic to these derivatives:</Heading>
              <Controller
                name="derivatives"
                control={control}
                render={({ field }) => {
                  {
                    !!errors.derivatives && (
                      <Text
                        variation="error"
                        as="p"
                        lineHeight="0.5em"
                        fontWeight={400}
                        fontSize="0.8em"
                        fontStyle="normal"
                        textDecoration="none"
                        width="30vw"
                        style={{ marginLeft: "10px" }}
                      >
                        {errors.derivatives.message}
                      </Text>
                    );
                  }
                  return (
                    <TagsInput
                      placeHolder="Tags"
                      value={field?.value || []}
                      onChange={(tags) => field.onChange(tags)}
                    />
                  );
                }}
              />
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <SwitchField
                    label="Is Active"
                    labelPosition="end"
                    {...field}
                    isChecked={field.value === 1}
                    onChange={(event) =>
                      field.onChange(event.target.checked ? 1 : 0)
                    }
                  />
                )}
              />
              <Button variation="primary" type="submit">
                Save
              </Button>
            </Flex>
          </FlexFormContainer>
        </form>
      </>
    );
  }
}
